import { faBuilding, faLocationDot, faPlus, faScroll, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import NumberFlow from '@number-flow/react';
import { useEffect, useState } from 'react';
import cn from 'classnames';
import { useSelectable } from 'hooks/useSelectable';
import { getLocations, createLocation, deleteLocations } from '@redux/features/locationsSlice';
import { addNotification } from '@redux/features/notificationsSlice';
import { deleteEmptyArrayValues } from 'helpers/deleteEmptyArrayValues';
import { trimObjectStrings } from 'helpers/trimObjectStrings';
import { validateField } from 'helpers/validateField';
import { TabSelector } from 'components/TabSelector';
import { NoResults } from 'components/NoResults';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { Modal } from 'components/Modal';
import { Table } from 'components/Table';
import styles from 'pages/Locations/Locations.scss';

export const Locations = () => {
  const [formData, setFormData] = useState({ name: '', address: '', description: '', halls: [''] });

  const { isLoading, error, locations } = useSelector((state) => state.locations);
  const { selectedIDs, toggleSelection, clearSelection } = useSelectable();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [locationTypeIndex, setLocationTypeIndex] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isMultiHall, setIsMultiHall] = useState(false);
  const [isFormValid, setIsFormValid] = useState(true);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();

  const locationTypes = { studio: 'Студія', affiliate: 'Філія' };

  useEffect(() => {
    dispatch(getLocations());
  }, []);

  useEffect(() => {
    if (error) {
      dispatch(
        addNotification({
          id: Date.now(),
          title: error,
          type: 'error',
        })
      );
    }
  }, [error]);

  const handleChange = (e, field, index) => {
    const { name, value } = e.target;

    setFormData((prevData) => {
      if (field) {
        const updatedField = [...prevData[field]];
        updatedField[index] = value;
        return { ...prevData, [field]: updatedField };
      } else {
        return { ...prevData, [name]: value };
      }
    });

    const fieldName = field || name;
    const fieldValue = field ? [...formData[field]] : value;
    const updatedValue = field ? [...fieldValue.slice(0, index), value.trim(), ...fieldValue.slice(index + 1)] : value;

    const newError = validateField(fieldName, updatedValue);
    if (hasSubmitted) {
      setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: newError }));
    }

    const hasErrors = Object.values({ ...errors, [fieldName]: newError }).some((err) => err);
    setIsFormValid(!hasErrors);
  };

  const addField = (field) => setFormData((prevData) => ({ ...prevData, [field]: [...prevData[field], ''] }));

  const removeField = (index, field) =>
    setFormData((prevData) => ({
      ...prevData,
      [field]: prevData[field].filter((_, i) => i !== index),
    }));

  const handleCreate = async (e) => {
    e.preventDefault();

    const cleanedData = trimObjectStrings({
      ...formData,
      halls: isMultiHall ? deleteEmptyArrayValues(formData.halls) : [formData.name],
    });

    const newErrors = {
      name: validateField('name', cleanedData.name),
      address: validateField('address', cleanedData.address),
      halls: validateField('halls', cleanedData.halls),
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) {
      setIsFormValid(false);
      setHasSubmitted(true);
      return;
    }

    const locationData = { ...cleanedData, type: Object.keys(locationTypes)[locationTypeIndex] };

    const result = await dispatch(createLocation(locationData));

    if (result.meta?.requestStatus === 'fulfilled') {
      setIsCreateModalOpen(false);
    }
  };

  // const handleUpdate = (id, field, value, previousValue) => {
  //   const updatedLocationData = {
  //     [field]: value,
  //   };

  //   dispatch(updateLocation({ id, updatedLocationData }))
  //     .unwrap()
  //     .catch(() => {
  //       document.getElementById(`${field}-${id}`).textContent = previousValue;
  //     });
  // };

  const handleDelete = async () => {
    if (selectedIDs.length > 0) {
      const result = await dispatch(deleteLocations(selectedIDs));

      if (result.meta.requestStatus === 'fulfilled') {
        dispatch(
          addNotification({
            id: Date.now(),
            title: 'Виділені локації успішно видалено',
            type: 'success',
          })
        );
        clearSelection();
      }
    }
  };

  // const handleEdit = (id, field, previousValue) => {
  //   const element = document.getElementById(`${field}-${id}`);
  //   const input = document.createElement('input');
  //   input.value = element.textContent;
  //   input.className = styles.inputInline;
  //   element.replaceWith(input);

  //   input.focus();

  //   input.addEventListener('blur', () => {
  //     const newValue = input.value.trim();
  //     if (newValue && newValue !== previousValue) {
  //       handleUpdate(id, field, newValue, previousValue);
  //       input.replaceWith(element);
  //     } else {
  //       input.replaceWith(element);
  //       element.textContent = previousValue;
  //     }
  //   });

  //   input.addEventListener('keydown', (e) => {
  //     if (e.key === 'Enter') {
  //       input.blur();
  //     }
  //   });
  // };

  const toggleMultiHall = () => setIsMultiHall((prev) => !prev);

  return (
    <>
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <h1>
            <FontAwesomeIcon icon={faLocationDot} />
            Локації
          </h1>
          <p>Огляд всіх локацій та залів</p>
        </div>
        <div className={styles.buttons}>
          <Button text='Створити' onClick={() => setIsCreateModalOpen(true)} />
          <Button text='Видалити' onClick={handleDelete} ghostStyle disabled={selectedIDs.length === 0} />
          {selectedIDs.length > 0 && (
            <p className={styles.selectedEntries}>
              Виділено <NumberFlow className={styles.count} value={selectedIDs.length} /> запис
              {selectedIDs.length >= 2 && selectedIDs.length <= 4 ? 'и' : selectedIDs.length > 4 ? 'ів' : ''}
            </p>
          )}
        </div>
      </div>
      {locations.length > 0 ? (
        <Table
          columns={[
            { key: 'checkbox', label: '' },
            { key: 'name', label: 'Назва' },
            { key: 'address', label: 'Адреса' },
            { key: 'description', label: 'Опис' },
            { key: 'type', label: 'Тип' },
            { key: 'hallsAmount', label: 'Зали' },
          ]}
          data={locations}
          selectedIDs={selectedIDs}
          renderRow={({ id, name, address, description, type, halls }, index) => (
            <>
              <td onClick={() => toggleSelection(id)}>{index + 1}</td>
              <td>{name}</td>
              <td>{address}</td>
              <td>{description}</td>
              <td>{locationTypes[type]}</td>
              <td>{halls.length}</td>
            </>
          )}
        />
      ) : (
        !isLoading && <NoResults />
      )}
      <Modal isOpen={isCreateModalOpen} setIsOpen={setIsCreateModalOpen}>
        <h3>Створити нову локацію</h3>
        <form className={styles.form} onSubmit={handleCreate}>
          <TabSelector tabs={Object.values(locationTypes)} onTabChange={setLocationTypeIndex} />
          <Input
            value={formData?.name}
            icon={faBuilding}
            name='name'
            placeholder='Назва локації'
            errorText={errors.name}
            invalid={errors.name}
            onChange={handleChange}
          />
          <Input
            value={formData?.address}
            icon={faLocationDot}
            name='address'
            placeholder='Адреса'
            errorText={errors.address}
            invalid={errors.address}
            onChange={handleChange}
          />
          <Input value={formData?.description} name='description' placeholder='Опис' onChange={handleChange} textarea />
          <label>
            <input type='checkbox' checked={isMultiHall} onChange={toggleMultiHall} />
            Локація має декілька залів
          </label>
          {isMultiHall && (
            <div className={styles.dynamicFields}>
              {formData.halls.map((value, index) => (
                <div className={styles.fieldRow} key={index}>
                  <Input
                    className={styles.dynamicInput}
                    value={value}
                    name='hall'
                    placeholder='Назва зали'
                    icon={faScroll}
                    errorText={errors.halls}
                    invalid={errors.halls}
                    onChange={(e) => handleChange(e, 'halls', index)}
                    autoComplete='off'
                  />
                  {index === 0 ? (
                    <button
                      className={cn(styles.quantityButton, styles.addBtn)}
                      type='button'
                      onClick={() => addField('halls')}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  ) : (
                    <button
                      className={cn(styles.quantityButton, styles.removeBtn)}
                      type='button'
                      onClick={() => removeField(index, 'halls')}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <Button className={styles.createBtn} text='Створити' type='submit' />
        </form>
      </Modal>
    </>
  );
};
